import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Clock, CheckCircle, XCircle, Loader2, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeaveRequests = () => {
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null);

    useEffect(() => {
        fetchLeaveRequests();
    }, []);

    const fetchLeaveRequests = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/leave`, { withCredentials: true });
            setLeaveRequests(response.data);
        } catch (error) {
            toast.error('Failed to load leave requests');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (id) => {
        setProcessing(id);
        try {
            await axios.put(`${API_URL}/api/leave/${id}/approve`, {}, { withCredentials: true });
            toast.success('Leave request approved');
            fetchLeaveRequests();
        } catch (error) {
            toast.error('Failed to approve request');
        } finally {
            setProcessing(null);
        }
    };

    const handleReject = async (id) => {
        setProcessing(id);
        try {
            await axios.put(`${API_URL}/api/leave/${id}/reject`, {}, { withCredentials: true });
            toast.success('Leave request rejected');
            fetchLeaveRequests();
        } catch (error) {
            toast.error('Failed to reject request');
        } finally {
            setProcessing(null);
        }
    };

    const pendingRequests = leaveRequests.filter(r => r.status === 'pending');
    const processedRequests = leaveRequests.filter(r => r.status !== 'pending');

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="admin-leave-requests-page">
                <div>
                    <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Leave Requests</h1>
                    <p className="text-[#4B5563]">Review and manage teacher leave requests</p>
                </div>

                {/* Pending Requests */}
                <div>
                    <h2 className="text-lg font-semibold text-[#1F2937] mb-4 flex items-center">
                        <Clock className="h-5 w-5 mr-2 text-[#F59E0B]" />
                        Pending Requests ({pendingRequests.length})
                    </h2>
                    {pendingRequests.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {pendingRequests.map((request) => (
                                <Card key={request.id} className="card-surface border-l-4 border-l-[#F59E0B]">
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center">
                                                <div className="h-10 w-10 rounded-full bg-[#064E3B] flex items-center justify-center text-white font-medium mr-3">
                                                    <User className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-[#1F2937]">Teacher Request</p>
                                                    <p className="text-xs text-[#4B5563]">
                                                        {new Date(request.created_at).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>
                                            <Badge className="bg-[#F59E0B]">Pending</Badge>
                                        </div>
                                        <div className="space-y-2 mb-4">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-[#4B5563]">From</span>
                                                <span className="font-medium tabular-nums">{request.start_date}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-[#4B5563]">To</span>
                                                <span className="font-medium tabular-nums">{request.end_date}</span>
                                            </div>
                                            <div className="pt-2">
                                                <p className="text-xs text-[#4B5563]">Reason</p>
                                                <p className="text-sm">{request.reason}</p>
                                            </div>
                                        </div>
                                        <div className="flex space-x-2">
                                            <Button
                                                size="sm"
                                                onClick={() => handleApprove(request.id)}
                                                disabled={processing === request.id}
                                                className="flex-1 bg-[#10B981] hover:bg-[#059669]"
                                            >
                                                {processing === request.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                                                Approve
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() => handleReject(request.id)}
                                                disabled={processing === request.id}
                                                className="flex-1"
                                            >
                                                {processing === request.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4 mr-1" />}
                                                Reject
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <Card className="card-surface">
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <CheckCircle className="h-12 w-12 text-[#10B981]/30 mb-4" />
                                <p className="text-[#4B5563]">No pending requests</p>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Processed Requests */}
                {processedRequests.length > 0 && (
                    <div>
                        <h2 className="text-lg font-semibold text-[#1F2937] mb-4">Previous Requests</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {processedRequests.map((request) => (
                                <Card key={request.id} className="card-surface opacity-75">
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <span className="text-sm text-[#4B5563]">{request.start_date} - {request.end_date}</span>
                                            <Badge className={request.status === 'approved' ? 'bg-[#10B981]' : 'bg-[#EF4444]'}>
                                                {request.status}
                                            </Badge>
                                        </div>
                                        <p className="text-sm text-[#4B5563]">{request.reason}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default LeaveRequests;
